import type { SiteAdapter, AdapterContext } from "../adapters/base.js";
import type { Url, ChapterIndex } from "../data-model/primitives.js";
import type { ChapterRef } from "../data-model/novel.js";
import type { EventBus } from "../core/events.js";
import { ChapterRefSchema } from "../data-model/schema.js";
import { ParseError } from "../core/errors.js";
import { walkToc } from "./walk-toc.js";

export interface FetchTocRange {
  readonly from?: ChapterIndex;
  readonly to?: ChapterIndex;
}

/**
 * Validates each raw ChapterRef from the adapter, then forwards in-range refs to
 * `onRef` via {@link walkToc}. Returns the count of forwarded (in-range) refs.
 * See walkToc for the ascending-index requirement.
 */
export async function fetchTocStage(
  adapter: SiteAdapter,
  url: Url,
  ctx: AdapterContext,
  bus: EventBus,
  onRef: (ref: ChapterRef) => Promise<void> | void,
  range?: FetchTocRange,
): Promise<number> {
  async function* validated(): AsyncIterable<ChapterRef> {
    for await (const raw of adapter.fetchChapterList(url, ctx)) {
      const parsed = ChapterRefSchema.safeParse(raw);
      if (!parsed.success) {
        throw new ParseError(`Invalid ChapterRef from adapter`, { cause: parsed.error, url });
      }
      yield parsed.data;
    }
  }
  return walkToc(validated(), {
    ...(range !== undefined ? { range } : {}),
    bus,
    signal: ctx.signal,
    onRef,
  });
}
